import { api } from '@/api';
import { canReadUsers, coreQueryKey } from '@/api/core/authorization';
import { readAdminUser, readAdminUsers, type AdminUserFilters } from '@/api/core/users';
import type { ServiceClient } from '@/api/http/client';
import { isApiError } from '@/api/http/errors';
import { operatorConfig, operatorSession } from '@/auth/runtime';
import type { OperatorSession } from '@/auth/session';

export interface UserRuntime {
  client: ServiceClient;
  session: OperatorSession;
  apiNamespace: string;
}

export const userRuntime: UserRuntime = {
  client: api.core,
  session: operatorSession,
  apiNamespace: operatorConfig?.coreApiUrl ?? 'unconfigured',
};

function authorizedUser(
  session: OperatorSession,
  generation: number,
  roleMask: number,
  bits: number,
): boolean {
  const state = session.getSnapshot();
  return (
    state.generation === generation &&
    state.status === 'authorized' &&
    !!state.authorization &&
    state.authorization.assigned &&
    !state.deniedDomains.includes('user') &&
    (state.authorization.role_bits & roleMask) !== 0 &&
    ((state.authorization.domain_bits.user ?? 0) & bits) === bits
  );
}

/** 목록·상세: STAFF/ADMIN + USER 2. */
export function hasUserAccess(session: OperatorSession, generation: number): boolean {
  const state = session.getSnapshot();
  return (
    authorizedUser(session, generation, 4 | 8, 2) &&
    !!state.authorization &&
    canReadUsers(state.authorization)
  );
}

/** 권한 조회·변경: STAFF/ADMIN + USER 1. 사용자 디렉터리 조회 권한(USER 2)과 독립이다. */
export const hasUserAuthorizationAccess = (session: OperatorSession, generation: number) =>
  authorizedUser(session, generation, 4 | 8, 1);

/** 이름·상태 변경: ADMIN + USER 4. */
export const hasUserProfileCommandAccess = (session: OperatorSession, generation: number) =>
  authorizedUser(session, generation, 8, 4);

async function readGuarded<T>(
  runtime: UserRuntime,
  generation: number,
  signal: AbortSignal,
  allowed: () => boolean,
  read: () => Promise<T>,
): Promise<T> {
  if (!allowed()) throw new Error('현재 사용자 관리 권한을 확인할 수 없습니다.');
  try {
    const result = await read();
    if (!allowed()) throw new Error('현재 사용자 관리 권한을 확인할 수 없습니다.');
    return result;
  } catch (error) {
    if (isApiError(error) && error.status === 403 && !signal.aborted && allowed())
      runtime.session.reportDomainAccessDenied('user', generation);
    throw error;
  }
}

export function userListQuery(
  runtime: UserRuntime,
  generation: number,
  filters: AdminUserFilters,
  enabled: boolean,
) {
  const allowed = () => hasUserAccess(runtime.session, generation);
  return {
    queryKey: [...coreQueryKey(runtime.apiNamespace, generation, 'users'), filters],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      readGuarded(runtime, generation, signal, allowed, () =>
        readAdminUsers(runtime.client, filters, signal),
      ),
    enabled: enabled && allowed(),
    meta: { suppressGlobalError: true },
  };
}

export function userDetailQuery(
  runtime: UserRuntime,
  generation: number,
  userId: string,
  enabled: boolean,
) {
  const allowed = () => hasUserAccess(runtime.session, generation);
  return {
    queryKey: coreQueryKey(runtime.apiNamespace, generation, 'user-detail', userId),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      readGuarded(runtime, generation, signal, allowed, () =>
        readAdminUser(runtime.client, userId, signal),
      ),
    enabled: enabled && allowed(),
    meta: { suppressGlobalError: true },
  };
}
